import { _getLogger } from '@doxyme/analytics';
import { Specs, attachHandler } from '@doxyme/extensions-common';
import { type Manifest } from '@doxyme/extensions-graphql-types';
import Instance, { type InstanceSource } from './instance';
import type Capability from './capability';
import { logErrorAndThrowBind, type LogErrorAndThrowBound } from './utils';

export interface AddInstanceOptions {
  source?: InstanceSource;
}

const logger = _getLogger('bridge', {
  component: 'extensions',
  lib: 'bridge',
});
const logErrorAndThrow: LogErrorAndThrowBound = logErrorAndThrowBind(logger);

const { System } = Specs;

type Constraints = Specs.Constraints;

type CapabilitiesChangedHandler = () => void;

/**
 * The Bridge is the central hub for all communication between the host application and the extensions.
 * It is responsible for registering Capabilities and Extensions, and for routing messages between them.
 */
export default class ExtensionBridge {
  readonly #instances: Instance[] = [];

  readonly #capabilities: Record<string, Capability> = {};

  readonly #capabilitiesChangedHandlers: CapabilitiesChangedHandler[] = [];

  /**
   * Creates a new Bridge
   * @param capabilities - The list of Capabilities to register. Capabilities can also be added after Bridge creation using the addCapability method.
   */
  constructor(capabilities: Capability[] = []) {
    for (const capability of capabilities) {
      this.addCapability(capability);
    }
  }

  getCapability<T>(capabilityId: string): T | undefined {
    return this.#capabilities[capabilityId] as T;
  }

  /**
   * Checks if a Capability is registered with the Bridge
   * @param capabilityId - The Capability ID to check
   * @returns Boolean indicating whether the Capability is registered
   */
  hasCapability(capabilityId: string): boolean {
    return capabilityId in this.#capabilities;
  }

  /**
   * Assign a listener to know when capability map is updated
   * @param handler - callback fn
   */
  onCapabilityChange(handler: CapabilitiesChangedHandler): () => void {
    return attachHandler(this.#capabilitiesChangedHandlers, handler);
  }

  handleCapabilitiesChange(): void {
    for (const handler of this.#capabilitiesChangedHandlers) {
      handler();
    }
  }

  /**
   * Registers a Capability with the Bridge, ensuring that all existing extensions that use the Capability are also added to the Capability's extension list
   * @param capability - The Capability to register
   * @description This is used in conjunction with removeCapability as Capabilities come online and offline as the user navigates through the application.
   * For example, the CallFrameAppletCapability is only available when the user is in a call.
   * @example
   * ```ts
   * bridge.addCapability(new ReceiveContextCapability());
   * ```
   */
  addCapability(capability: Capability): void {
    if (this.#capabilities[capability.id]) {
      logErrorAndThrow(
        `[addCapability] Capability already registered: ${capability.id}`,
      );
    }

    capability.clear();

    this.#capabilities[capability.id] = capability;

    for (const instance of this.#instances) {
      if (instance.usesCapability(capability.id)) {
        capability.addInstance(instance);

        // trigger system events for related instances
        instance.notify([System.Events.CapabilityOnline, capability.id]);
        this.notifyCapabilitiesOnline(instance);
      }
    }

    this.handleCapabilitiesChange();
  }

  /**
   * Removes a Capability from the Bridge and clears the list of extensions on the Capability
   * @param capabilityId - The capability ID to remove
   * @description This is used in conjunction with addCapability as Capabilities come online and offline as the user navigates through the application.
   * For example, the CallFrameAppletCapability is only available when the user is in a call.
   */
  removeCapability(capabilityId: string): void {
    const capability = this.#capabilities[capabilityId];

    if (!capability) {
      logErrorAndThrow(
        `[removeCapability] Capability not registered: ${capabilityId}`,
      );
    }

    delete this.#capabilities[capabilityId];

    capability.clear();

    // trigger system events for related instances
    for (const instance of this.#instances) {
      if (instance.usesCapability(capability.id)) {
        instance.notify([System.Events.CapabilityOffline, capability.id]);
        this.notifyCapabilitiesOnline(instance);
      }
    }

    this.handleCapabilitiesChange();
  }

  /**
   * Checks if an extension is registered with the bridge
   * @param instanceId - The extension ID to check
   * @returns Boolean indicating whether the extension is registered
   */
  hasInstance(instanceId: string): boolean {
    return this.#instances.some((i) => i.id === instanceId);
  }

  /**
   * Checks if manifest is valid and was not registered yet, is called before adding an instance
   * @param manifest - The extension's manifest to check
   */
  checkManifest(manifest: Manifest): void {
    const isValidManifestResponse = Specs.isValidManifest(manifest);
    if (!isValidManifestResponse.isValid) {
      logErrorAndThrow(
        `[checkManifest] Invalid manifest for "${manifest.name}", Error: ${
          isValidManifestResponse.error ?? ''
        }`,
      );
    }

    if (this.hasInstance(manifest.id)) {
      logErrorAndThrow(
        `[checkManifest] Instance already registered: ${manifest.name}`,
      );
    }
  }

  /**
   * Adds an extension to the bridge, ensuring that the extension is registered with all Capabilities referenced in the manifest.
   * @param manifest - The extension's manifest to register
   * @param options - Optional configuration including the source that loaded this instance
   * @example
   * ```ts
   * bridge.addInstance({
   *  id: 'my-extension',
   *  capabilities: [{type: 'receive-context', context: ['role']}],
   * });
   * ```
   */
  addInstance(manifest: Manifest, options?: AddInstanceOptions): Instance {
    this.checkManifest(manifest);

    const instance = new Instance(this, manifest, options?.source);

    this.#instances.push(instance);

    for (const capability of Object.values(this.#capabilities)) {
      if (instance.usesCapability(capability.id)) {
        capability.addInstance(instance);
      }
    }

    return instance;
  }

  /**
   * Adds multiple extensions to the bridge, ensuring that all are registered with all Capabilities referenced in the manifest.
   * This function will fail if any single manifest is not addable to the bridge.
   * @param manifests - An array of extension manifests to register
   * @param options - Optional configuration including the source that loaded these instances
   */
  addAllInstances(
    manifests: Manifest[],
    options?: AddInstanceOptions,
  ): Instance[] {
    for (const manifest of manifests) {
      this.checkManifest(manifest);
    }
    return manifests.map((m) => this.addInstance(m, options));
  }

  /**
   * Removes an extension from the bridge, ensuring the extension is also removed from any Capability that it's currently registered with.
   * @param instanceId - The extension ID to remove
   */
  removeInstance(instanceId: string): void {
    const instanceIndex = this.#instances.findIndex((i) => i.id === instanceId);
    const instance = this.#instances[instanceIndex];

    if (!instance) {
      logErrorAndThrow(
        `[removeInstance] Instance not registered: ${instanceId}`,
      );
    }

    this.#instances.splice(instanceIndex, 1);

    for (const capability of Object.values(this.#capabilities)) {
      if (instance.usesCapability(capability.id)) {
        capability.removeInstance(instance);
      }
    }
  }

  /**
   * Removes all extension instances from the bridge, including deregistering them from any Capabilities they are associated with.
   */
  removeAllInstances(): void {
    for (const id of this.#instances.map((i) => i.id)) {
      this.removeInstance(id);
    }
  }

  /**
   * Returns an array of all registered instance IDs.
   */
  getInstanceIds(): string[] {
    return this.#instances.map((i) => i.id);
  }

  /**
   * Finds an existing extension instance by its manifest name.
   *
   * @param name - The manifest name to search for
   * @returns The instance ID if found, undefined otherwise
   */
  findInstanceByName(name: string): string | undefined {
    return this.#instances.find((i) => i.manifest.name === name)?.id;
  }

  /**
   * Returns instance if added
   * @param instanceId - The extension ID to get
   */
  /**
   * @hidden
   */
  getInstance(instanceId: string): Instance {
    const instance = this.#instances.find((i) => i.id === instanceId);
    if (!instance) {
      logErrorAndThrow(`[getInstance] Instance not registered: ${instanceId}`);
    }
    return instance;
  }

  /* HM 2023-12-05:
     receive() hard fails on several checks here because it should not be the first-line defense against abuse.
     Invalid data that reaches this method indicate a more serious bug.
  */
  /**
   * @hidden
   */
  receive(instance: Instance, tuple: Constraints['actionPayloadTuple']): void {
    const [type] = tuple;
    const spec = Specs.byAction(type);
    const capability = this.#capabilities[spec.id];

    const instanceContext = {
      instanceId: instance.id,
      instanceName: instance.manifest.name,
      actionType: type,
      capabilityId: spec.id,
    };

    if (!capability) {
      logErrorAndThrow(
        `[receive] Unregistered Capability: ${spec.id}`,
        instanceContext,
      );
    }

    if (!instance.usesCapability(spec.id)) {
      logErrorAndThrow(
        `[receive] Instance does not use capability: ${spec.id}`,
        instanceContext,
      );
    }

    capability.receive(instance, tuple);
  }

  /**
   * Trigger system event CapabilitiesOnline
   * @param instance - The extension instance
   */
  /**
   * @hidden
   */
  notifyCapabilitiesOnline(instance: Instance): void {
    instance.notify([
      System.Events.CapabilitiesOnline,
      Object.keys(this.#capabilities).filter(
        instance.usesCapability.bind(instance),
      ),
    ]);
  }
}
